import { Button } from '@/components/ui'
import { ReconciliationBadge } from '@/modules/projects'
import { shownReconciliationStatus } from '@/modules/projects/status/statement_segment'
import type { StatementSegment } from '@/modules/projects/stores/types'
import { consecutiveRuns } from '@/utils'
import { AccountField } from './AccountField'
import { AccountVerdicts } from './AccountVerdicts'
import { Fact } from './Fact'
import { FACTS_GRID } from './mapLayout'
import { filesOf, pagesReadOut, sentenceCase } from './moves'
import { PeriodField } from './PeriodField'
import type { Account, DatePeriod, DocumentPage, StatementView } from './types'

interface StatementBarProps {
  view: StatementView | null
  pages: DocumentPage[]
  accounts: Account[]
  // The cycle lukita holds for this statement, or null for one the user started here.
  segment: StatementSegment | null
  isFixable: boolean
  onSetPeriod: (period: DatePeriod) => void
  onSetAccount: (accountId: string) => void
  onAddAccount: () => void
  onReview: () => void
}

// Where the statement's pages were scanned. The upload is how anyone opens the real PDF.
const scannedIn = (pages: DocumentPage[]) => {
  const files = filesOf(pages)
  return files.length === 1 ? files[0] : `Across ${files.length} files`
}

const pagesFact = (view: StatementView, pages: DocumentPage[]) => {
  const read = sentenceCase(pagesReadOut(pages))
  const places = consecutiveRuns(view.pages).length
  return places > 1 ? `${read}, in ${places} places` : read
}

// What the open statement says about itself, one named fact a row, the same way the map names an
// account. The two a person can correct come first, drawn as controls.
export const StatementBar = ({
  view,
  pages,
  accounts,
  segment,
  isFixable,
  onSetPeriod,
  onSetAccount,
  onAddAccount,
  onReview,
}: StatementBarProps) => {
  if (!view) return null

  return (
    <div className="flex flex-col gap-4">
      <dl className={FACTS_GRID}>
        <Fact label="Account">
          <AccountField
            accounts={accounts}
            accountId={view.statement.accountId}
            onPick={onSetAccount}
            onAddNew={onAddAccount}
          />
        </Fact>
        <Fact label="Dates">
          <PeriodField period={view.statement.period} onChange={onSetPeriod} />
        </Fact>
        {segment ? (
          <Fact label="Status">
            <div>
              <ReconciliationBadge
                status={shownReconciliationStatus(segment)}
              />
              <AccountVerdicts segment={segment} />
            </div>
          </Fact>
        ) : null}
        <Fact label="Pages">{pagesFact(view, pages)}</Fact>
        <Fact label="File">
          <span className="truncate text-ink-subtle" title={scannedIn(pages)}>
            {scannedIn(pages)}
          </span>
        </Fact>
      </dl>

      {/* Fixing is the one job worth the loud button. Looking stays quiet. */}
      {segment ? (
        <Button theme={isFixable ? 'default' : 'outline'} onClick={onReview}>
          {isFixable ? 'Review & fix' : 'View cycle'}
        </Button>
      ) : null}
    </div>
  )
}
